import {STUDENT_PROFILE_CONFIRMATION_TYPES} from '../../const/StudentProfileConfirmationTypes';
import ConfirmationWrapper from '../../Components/ConfirmationWrapper';
import {useGetConsentsQuery, useUpdateConsentsMutation} from '../../../../../api/services/studentService';
import {useParams} from 'react-router-dom';
import React, {useEffect, useMemo, useState} from 'react';
import useNotification from '../../../../../utility/hooks/useNotification';
import {Button, Card, Checkbox, TextField, Tooltip} from '@mui/material';
import FileTypeIcon from '../../../../../shared-components/FileTypeIcon';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import {downloadConsentFile} from '../../../../../api/services/filesService';
import {useGetProfileDescriptionQuery} from '../../../../../api/services/schoolManagementService';
import {
    DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS,
    PROFILE_SECTIONS_KEYS
} from '../../../../../constants/ProfileSectionsKeys';
import formatProfileDescription from '../../../../../utility/profileDescriptionFormatter';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import DocumentPreviewDialog from '../../../../../shared-components/DocumentPreviewDialog';
import {getHisHerPronoun} from '../../../../../utility/pronounUtil';
import ProfileSectionHeading from '../ProfileSectionHeading';

const Consents = (
    {
        readonly,
        profile,
        school,
        onConfirmSuccess
    }) => {
    const {studentId} = useParams();
    const {
        data: consentsData = {},
        isLoading: isConsentsLoading,
        isFetching: isConsentsFetching,
        refetch: refetchConsentsData
    } = useGetConsentsQuery(studentId);
    const [updateConsents, {
        isSuccess: isUpdateConsentsSuccess,
        isError: isUpdateConsentsError
    }] = useUpdateConsentsMutation();
    const {
        data: consentDescriptionData
    } = useGetProfileDescriptionQuery(PROFILE_SECTIONS_KEYS.parentalConsents);
    const consentDescription = consentDescriptionData?.value ?? DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.parentalConsents];
    const [comment, setComment] = useState('');
    const [isEdited, setIsConsentFieldsEdited] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState({blob: null, filename: ''});
    const [documentIsLoading, setDocumentIsLoading] = useState(false);
    const [openDocumentPreviewDialog, setOpenDocumentPreviewDialog] = useState(false);
    const [selectedConsentsCodes, setSelectedConsentsCodes] = useState([]);
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const isLoading = isConsentsLoading || isConsentsFetching;

    useEffect(() => {
        if (consentsData.comment) {
            setComment(consentsData.comment);
        }
        if (consentsData.consents) {
            setSelectedConsentsCodes(consentsData.consents
                .filter(c => c.isChecked)
                .map(c => c.code)
            );
        }
    }, [consentsData]);

    useEffect(() => {
        if (isUpdateConsentsSuccess) {
            showSuccessNotification('Consents updated successfully');
        } else if (isUpdateConsentsError) {
            showErrorNotification('Failed to update consents');
        }
    }, [isUpdateConsentsSuccess, isUpdateConsentsError]);

    const handleChangeCheckbox = (consentCode, isChecked) => {
        setIsConsentFieldsEdited(true);
        setSelectedConsentsCodes(isChecked
            ? [...selectedConsentsCodes, consentCode]
            : selectedConsentsCodes.filter(code => code !== consentCode));
    };

    const handleChangeComment = (comment) => {
        setComment(comment);
        setIsConsentFieldsEdited(true);
    };

    const handleSave = async () => {
        const activeCodes = consentsData.consents
            .filter(x => selectedConsentsCodes.includes(x.code))
            .map(x => ({code: x.code, isGlobal: x.isGlobalConsent}));

        await updateConsents({studentId, body: {activeCodes, studentId, comment}});
        setIsConsentFieldsEdited(false);
    };

    const consentOptions = useMemo(() => {
        if (!consentsData.consents) return [];

        return [...consentsData.consents]
            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
            .map(x => ({...x, description: x.description?.replaceAll('@studentForename', profile.name)}));
    }, [consentsData, profile]);

    const previewDocument = async (consent) => {
        setDocumentIsLoading(true);
        setOpenDocumentPreviewDialog(true);
        setSelectedDocument(prev => ({...prev, filename: consent.file.name}));

        const res = await downloadConsentFile(consent.code);

        setSelectedDocument(prev => ({
            ...prev,
            blob: res?.blob ? new Blob([res.blob], {type: res.mimeType || res.blob.type || 'application/octet-stream'}) : null
        }));
        setDocumentIsLoading(false);
    };

    return (
        isLoading
            ? ''
            : <ConfirmationWrapper
                readonly={readonly}
                confirmData={{
                    confirmMessage: `I confirm that the information in the consents profile section of ${profile.name} ${profile.surname}'s student profile is correct and understand it will be relied upon by school staff and any other person or organisation who may have ${profile.name} ${profile.surname} in their care on behalf of ${school.name}`,
                    confirmedAt: consentsData?.consentsConfirmedAt,
                    confirmedBy: consentsData?.consentsConfirmedBy,
                    enableSaveButton: isEdited,
                    studentId,
                    type: STUDENT_PROFILE_CONFIRMATION_TYPES.Consents,
                    handleSave,
                    onConfirmSuccess: async () => {
                        await onConfirmSuccess();
                        await refetchConsentsData();
                    },
                    confirmButtonText: 'Confirm Consents'
                }}
                // Maria, 18 October 2026: the confirm box below the section, on the left
                actionsBelow
                heading={(
                    <Box sx={{flex: '1 1 360px', minWidth: 0}}>
                        {consentDescription
                            ? <Typography variant="body1" sx={{whiteSpace: 'pre-line'}}>
                                {formatProfileDescription(
                                    consentDescription,
                                    {
                                        schoolName: school.name,
                                        studentName: profile.name,
                                        hisHer: getHisHerPronoun(profile.gender)
                                    })
                                }
                            </Typography>
                            : null}
                    </Box>
                )}
            >
                <Stack flexDirection="column" gap={1.5}>
                    <Box>
                        {/* Maria, 17 October 2026: one tidy row per consent, its policy opened with 'View'.
                            18 October 2026: the consents in three columns where the screen is wide enough. Each
                            consent draws its own right and bottom line; the card clips the lines on its outer edge. */}
                        <Card variant="outlined" sx={{mt: 1, borderRadius: 2, display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))'}}}>
                            {consentOptions
                                .filter(consent => !(profile.isApplicant && !consent.isApplicantVisible))
                                .map((consent, index) => (
                                    // Maria, 18 October 2026: the same look as the Transport table
                                    <Stack
                                        key={consent?.name}
                                        component="label"
                                        direction="row"
                                        gap={1.5}
                                        data-testid="consent-row"
                                        alignItems="center"
                                        sx={{
                                            px: 2,
                                            py: 1,
                                            cursor: readonly ? 'default' : 'pointer',
                                            backgroundColor: selectedConsentsCodes.includes(consent?.code) ? '#eaf2fb' : 'transparent',
                                            boxShadow: '1px 0 0 #eef0f3, 0 1px 0 #eef0f3'
                                        }}
                                    >
                                        <Checkbox
                                            sx={{p: 0}}
                                            checked={selectedConsentsCodes.includes(consent?.code)}
                                            onChange={e => handleChangeCheckbox(consent?.code, e.target.checked)}
                                            disabled={readonly}
                                            inputProps={{'aria-label': consent?.name}}
                                        />
                                        <Box sx={{flex: 1, minWidth: 0}}>
                                            <Typography fontWeight={selectedConsentsCodes.includes(consent?.code) ? 600 : 400}>{consent?.name}</Typography>
                                            {consent?.description?.trim() && (
                                                <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'pre-line'}}>
                                                    {consent.description.trim()}
                                                </Typography>
                                            )}
                                        </Box>
                                        {/* Maria, 18 October 2026: the policy on the same line as the consent, on the right,
                                            so every consent is the same height and lines up */}
                                        {consent.file != null && (
                                            <Tooltip title={consent.file.name} arrow>
                                                <Button
                                                    variant="text"
                                                    size="small"
                                                    startIcon={<FileTypeIcon name={consent.file.name} sx={{fontSize: 18}}/>}
                                                    onClick={event => {
                                                        event.preventDefault();
                                                        previewDocument(consent);
                                                    }}
                                                    sx={{flexShrink: 0, py: 0, px: 1, fontSize: 13, textTransform: 'none', whiteSpace: 'nowrap'}}
                                                >
                                                    View policy
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Stack>
                                ))}
                        </Card>
                    </Box>
                    <Box>
                        <ProfileSectionHeading sub sx={{mt: 0}}>Comments</ProfileSectionHeading>
                        <TextField
                            multiline
                            fullWidth
                            value={comment}
                            disabled={readonly}
                            onChange={(e) => e.target.value.length < 255 && handleChangeComment(e.target.value)}
                        />
                    </Box>
                </Stack>
                <DocumentPreviewDialog
                    overPage
                    maxWidth="md"
                    open={openDocumentPreviewDialog}
                    onClose={() => {
                        setOpenDocumentPreviewDialog(false);
                        setSelectedDocument({blob: null, filename: ''});
                    }}
                    document={selectedDocument?.blob}
                    documentName={selectedDocument?.filename}
                    isLoading={documentIsLoading}
                />
            </ConfirmationWrapper>
    );
};

Consents.propTypes = {
    readonly: PropTypes.bool,
    profile: PropTypes.object.isRequired,
    school: PropTypes.object.isRequired,
    onConfirmSuccess: PropTypes.func,
};

export default Consents;